import React from 'react';
import PropTypes from 'prop-types';
import { useNavListItems } from '@app/app-store';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCpId, useCpName } from '@app/profile-selection-store';
import { useFetchProfileSelectionQuery, findPrincipalGroupById, getPrimaryCpId } from '@pages/api/fetch-profile-selection';
import { Typography } from '@leegality/leegality-react-component-library';
import { BackButton } from '@components/back-button';
import { RL } from '@constants';
import NavElement from '../nav-element/nav-element';

const Sidebar = ({ onNavigate }) => {
  const navListItems = useNavListItems();
  const cpId = useCpId();
  const cpName = useCpName();
  const [searchParams] = useSearchParams();
  const principalGroupIdFromParams = searchParams.get('principalGroupId');
  const navigate = useNavigate();

  // Deep link / reload only: the dashboard copies the id from the URL into the
  // store before anything else is hydrated, so the id alone can't tell us
  // whether a fetch is needed. cpId is only ever set from a fetched group, and
  // displayName can legitimately be empty, so cpId is the hydration signal.
  const shouldFetch = principalGroupIdFromParams && !cpId;
  const { data } = useFetchProfileSelectionQuery(shouldFetch ? principalGroupIdFromParams : null);

  const profile = findPrincipalGroupById(data?.data?.records, principalGroupIdFromParams);
  const renderNavElements = (elements, isChild = false) => elements.map((element) => (
    <React.Fragment key={element.id}>
      <NavElement
        id={element.id}
        icon={element.icon}
        labelKey={element.labelKey}
        link={element.link}
        isChild={isChild}
        hasChild={element.children.length > 0}
        keepHidden={element?.keepHidden || false}
        onNavigate={onNavigate}
      />
      {element.children?.length > 0 && renderNavElements(element.children, true)}
    </React.Fragment>
  ));

  return (
    <div className="flex flex-col bg-white shadow-lg w-[240px] h-full pt-5 px-5" id="sidebar">
      <div className="mb-3 flex items-center gap-3">
        <BackButton
          onClick={() => { navigate(`/${RL.SELECT_PROFILE}`); onNavigate?.(); }}
          className="flex-shrink-0"
        />
        <Typography
          tag={Typography.Tags.P}
          color={Typography.Colors.gray900}
          variant={Typography.Variants.sm}
          fontWeight={Typography.FontWeight.bold}
          className="truncate"
        >
          {cpName || profile?.displayName || cpId || getPrimaryCpId(profile) || '-'}
        </Typography>
      </div>
      <div>{renderNavElements(navListItems)}</div>
    </div>
  );
};

Sidebar.defaultProps = {
  onNavigate: null,
};

Sidebar.propTypes = {
  onNavigate: PropTypes.func,
};

export default Sidebar;
